import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useHistory, Switch, Route } from 'react-router-dom';
// mui components
import { Grid, Tabs, Tab, Box, TabProps, Button, ButtonProps, Hidden } from '@mui/material';
import { styled } from '@mui/material/styles';
// sections
import AdminSection from './sections/admin-section';
import DealsSection from './sections/deals-section';
import ProfileSection from './sections/profile-section';
import AccountSection from './sections/account-section';
import EducationSection from './sections/education-section';
import AnalyticsSection from './sections/analytics-section';
import ReferralSection from './sections/referral-section';
import PerksSection from './sections/perks-section';
import DealDetailsSection from './sections/deal-details-section';
import CollabClubSection from './sections/collab-club-section';
import PublishingSection from './sections/publishing-section';
import TourBudgetSection from './sections/tour-budget-section';
// custom hooks
import { useUserData } from 'hooks/useUserData';
import { useArtistProfiles } from 'hooks/swrRequests/useArtistProfiles';
// icons
import { ReactComponent as DealIcon } from 'assets/icons/deal_2.svg';
import { ReactComponent as MusicIcon } from 'assets/icons/music.svg';
import { ReactComponent as EducationIcon } from 'assets/icons/education.svg';
import { ReactComponent as AnalyticsIcon } from 'assets/icons/analytics.svg';
import { ReactComponent as ReferralIcon } from 'assets/icons/referral.svg';
import { ReactComponent as AdminIcon } from 'assets/icons/admin.svg';
import { ReactComponent as PerksIcon } from 'assets/icons/perks.svg';
import { ReactComponent as LogOutIcon } from 'assets/icons/log-out.svg';
import { Calculator, ListMusic, Lock, Users } from 'lucide-react';
// styles
import { colors } from 'styles/colors';
import ViewCourse from './sections/education-section/view-course';
import usePageTimeTracker from 'hooks/usePageTimeTracker';
import { useTranslation } from 'react-i18next';

const timeTrackerBuckets = {
  timer_1m: 60,
  timer_2m: 120,
  timer_5m: 300,
  timer_10m: 600,
};

interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
  activeValue: string;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, activeValue, ...other } = props;

  return (
    <Box
      display="flex"
      position="relative"
      width={value !== activeValue ? '0' : '100%'}
      minWidth={0}
      role="tabpanel"
      hidden={value !== activeValue}
      id={`vertical-tabpanel-${activeValue}`}
      aria-labelledby={`vertical-tab-${activeValue}`}
      {...other}
    >
      {value === activeValue && children}
    </Box>
  );
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  width: '218px',
  paddingTop: '40px',
  backgroundColor: colors.main.black,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.colors.main.yellow,
    left: 0,
    right: 'unset',
  },
}));

const StyledTab = styled((props: TabProps) => <Tab disableRipple {...props} />)(({ theme }) => ({
  justifyContent: 'flex-start',
  textAlign: 'left',
  minHeight: '56px',
  marginBottom: '16px',
  padding: '12px 27px',
  ...theme.fontStyles.subtext,
  color: theme.colors.text.light.placeholder,
  textTransform: 'unset',
  '&.Mui-selected': {
    color: theme.colors.main.white,
    '& path': {
      fill: theme.colors.main.white,
    },
  },
  '& .MuiTab-iconWrapper': {
    marginRight: '16px',
  },
  '&.Mui-disabled': {
    color: '#5c5860',
  },
}));

const StyledButton = styled(({ className, onClick, children }: ButtonProps) => (
  <Button
    disableRipple
    className={className}
    startIcon={<LogOutIcon />}
    onClick={onClick}
    children={children}
  />
))(({ theme }) => ({
  justifyContent: 'flex-start',
  minHeight: '56px',
  marginBottom: '16px',
  padding: '12px 27px',
  ...theme.fontStyles.subtext,
  color: theme.colors.text.light.placeholder,
  textTransform: 'unset',
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: '16px',
  },
}));

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const { logOut, permissionsData, onboardingInProgress } = useUserData();

  // Locked until at least one artist profile is completed (new artists only).
  const { anyCompleted } = useArtistProfiles(!onboardingInProgress);
  const locked = onboardingInProgress && !anyCompleted;

  const [value, setValue] = useState<string>('deals');

  usePageTimeTracker(value, timeTrackerBuckets);

  useEffect(() => {
    const [, pageName] = history.location.pathname.split('/');
    setValue(pageName);
  }, [history]);

  const handleChangeTab = useCallback(
    (e: SyntheticEvent, page: string) => {
      if (locked && page !== 'profile') return;
      setValue(page);
      history.replace({ pathname: `/${page}` });
    },
    [history, locked]
  );

  const tabLabel = useCallback(
    (label: string) =>
      locked ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {label}
          <Lock size={13} />
        </Box>
      ) : (
        label
      ),
    [locked]
  );

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      sx={{ minHeight: 'calc(100vh - 72px)', backgroundColor: '#1A0034' }}
    >
      <Grid
        container
        wrap="nowrap"
        flexGrow={1}
        sx={{ backgroundColor: colors.main.white }}
        className="container mx-auto"
      >
        <Hidden smDown>
          <StyledTabs
            // /account has no sidebar tab (it's reached via the avatar/drawer menus) — false = no selection
            value={value === 'account' ? false : value}
            variant="scrollable"
            orientation="vertical"
            onChange={handleChangeTab}
            aria-label="Vertical tabs"
            sx={{ borderRight: 1, borderColor: 'divider', flexShrink: 0 }}
          >
            <StyledTab
              label={tabLabel(t('Deals'))}
              icon={<DealIcon />}
              iconPosition="start"
              value="deals"
              disabled={locked}
            />
            <StyledTab
              label={t('Profile')}
              icon={<MusicIcon />}
              iconPosition="start"
              value="profile"
            />
            <StyledTab
              label={tabLabel(t('Perks'))}
              icon={<PerksIcon />}
              iconPosition="start"
              value="perks"
              disabled={locked}
            />
            {permissionsData.education && (
              <StyledTab
                label={tabLabel(t('Education'))}
                icon={<EducationIcon />}
                iconPosition="start"
                value="education"
                disabled={locked}
                onClick={() => {
                  if (locked) return;
                  if (window.location.pathname.trim() !== '/education') {
                    window.location.href = '/education';
                  }
                }}
              />
            )}

            {permissionsData.analytics && (
              <StyledTab
                label={tabLabel(t('Analytics'))}
                icon={<AnalyticsIcon />}
                iconPosition="start"
                value="analytics"
                disabled={locked}
              />
            )}

            <StyledTab
              label={tabLabel(t('Publishing'))}
              icon={<ListMusic size={24} />}
              iconPosition="start"
              value="publishing"
              disabled={locked}
            />

            <StyledTab
              label={tabLabel(t('Referral'))}
              icon={<ReferralIcon />}
              iconPosition="start"
              value="referral"
              disabled={locked}
            />

            <StyledTab
              label={tabLabel(t('Collab Club'))}
              icon={<Users size={24} />}
              iconPosition="start"
              value="collab-club"
              disabled={locked}
            />

            {permissionsData.education && (
              <StyledTab
                label={tabLabel(t('Tour budget'))}
                icon={<Calculator size={24} />}
                iconPosition="start"
                value="tour-budget"
                disabled={locked}
              />
            )}

            {permissionsData.admin && (
              <StyledTab
                label={tabLabel(t('Admin'))}
                icon={<AdminIcon />}
                iconPosition="start"
                value="admin"
                disabled={locked}
              />
            )}

            <StyledButton onClick={logOut}>{t('Log out')}</StyledButton>
          </StyledTabs>
        </Hidden>

        <TabPanel value="dashboard" activeValue="dashboard">
          <Switch>
            <Route exact path="/deals" component={DealsSection} />
            <Route exact path="/deals/:id" component={DealDetailsSection} />
            <Route exact path="/profile" component={ProfileSection} />
            <Route exact path="/account" component={AccountSection} />
            <Route exact path="/education" component={EducationSection} />
            <Route exact path="/education/:slug" component={ViewCourse} />
            <Route exact path="/analytics" component={AnalyticsSection} />
            <Route exact path="/perks" component={PerksSection} />
            <Route exact path="/referral" component={ReferralSection} />
            <Route exact path="/collab-club" component={CollabClubSection} />
            <Route exact path="/tour-budget" component={TourBudgetSection} />
            <Route exact path="/publishing" component={PublishingSection} />
            <Route exact path="/admin" component={AdminSection} />
          </Switch>
        </TabPanel>
      </Grid>
    </Grid>
  );
};

export default Profile;
